import { Formik, Field, Form } from "formik";
import axios from "axios";
import React, { useEffect, useState } from "react";
import SweetAlert from "react-bootstrap-sweetalert";
import * as yup from "yup";
import { toast } from "react-toastify";
import {
  Button,
  Col,
  Container,
  Label,
  Row,
  Spinner,
} from "reactstrap";
import AuthNavBar from "../../components/navbars/AuthNavBar";
import MobileNav from "../../components/navbars/MobileNav";
import BasicTemplate from "./BasicTemplate";
import { useLocation } from "react-router-dom";

axios.defaults.baseURL = `${process.env.REACT_APP_BE_URL}/api`;
axios.defaults.withCredentials = true;

const Private = (props) => {
  const { page } = props;
  const pitchPageId = page.pitch_page_id;
  const [showModal, setShowModal] = useState(false);
  const [standaloneAccess, setStandaloneAccess] = useState(false);
  const [publicPageData, setPublicPageData] = useState(page);
  const [isLoading, setIsLoading] = useState(false);
  const search = useLocation().search;
  const investorId = new URLSearchParams(search).get("investor_id");
  const token = new URLSearchParams(search).get("token");
  const accessCode = new URLSearchParams(search).get("accessCode");

  const requestStandaloneAccess = async (values) => {
    try {
      const response = await axios.post("/request-standalone-access", {
        pitch_page_id: pitchPageId,
        first_name: values.firstName,
        last_name: values.lastName,
        email: values.email,
        recipient_type_id: 12,
      });
      toast.success(response?.data?.message);
    } catch (err) {
      console.error(err);
      toast.error(err.response.data.message);
    } finally {
      setShowModal(false);
    }
  };

  useEffect(() => {
    if (investorId && token) {
      setIsLoading(true);
      const checkStandaloneAccess = async () => {
        try {
          const response = await axios.get(
            `/check-standalone-access/${token}/${investorId}/${pitchPageId}`
          );
          const pageData = await response?.data?.page_data;
          setStandaloneAccess(true);
          setPublicPageData(pageData);
        } catch (error) {
          console.error(error);
        } finally {
          setIsLoading(false);
        }
      };
      checkStandaloneAccess();
    }

    if (accessCode) {
      setIsLoading(true);
      const checkStandaloneCode = async () => {
        try {
          const response = await axios.get(
            `/use-standalone-code/${pitchPageId}/${accessCode}`
          );
          const pageData = await response?.data?.page_data;
          setStandaloneAccess(true);
          setPublicPageData(pageData);
        } catch (error) {
          console.error(error);
        } finally {
          setIsLoading(false);
        }
      };
      checkStandaloneCode();
    }
  }, []);

  const requestAccessWithCode = async (values) => {
    try {
      const response = await axios.get(
        `/use-standalone-code/${pitchPageId}/${values?.accessCode}`
      );
      const pageData = await response?.data?.page_data;
      setStandaloneAccess(true);
      setPublicPageData(pageData);
    } catch (error) {
      toast.error(error?.response?.data?.message);
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return <Spinner color="default" className="page-loader" />;
  }

  return standaloneAccess ? (
    <BasicTemplate page={publicPageData} />
  ) : (
    <Container>
      <MobileNav page={page} />
      <AuthNavBar page={page} />
      <h1 className="text-center mb-1" style={{ marginTop: 100 }}>
        <strong>{`Welcome to ${page?.name}`}</strong>
      </h1>
      <h4 className="text-center mt-2">
        This PitchPage is currently set to private
      </h4>
      <Col className="d-flex flex-column align-items-center">
        <Button
          onClick={() => setShowModal(true)}
          className="btn-lg btn-secondary btn-round"
          style={{ backgroundColor: page.primary_color, minWidth: "200px" }}
        >
          Request Access
        </Button>
        <p className="mt-2">OR</p>
        <Formik
          initialValues={{ accessCode: "" }}
          onSubmit={async (values, { setSubmitting }) => {
            await requestAccessWithCode(values);
            setSubmitting(false);
          }}
          validationSchema={yup.object().shape({
            accessCode: yup.string().required("Access code is required."),
          })}
        >
          {({
            values,
            errors,
            touched,
            isSubmitting,
            setFieldValue,
            handleReset,
          }) => (
            <Form className="d-flex flex-column align-items-center">
              <p className="text-center mb-2">Enter an Access Code</p>
              <Field
                name="accessCode"
                type="text"
                className="form-control"
                style={{ height: 40, fontSize: 16 }}
                value={values.accessCode}
              />
              {touched.accessCode && errors.accessCode && (
                <div
                  className="error mt-1"
                  style={{ color: "#dc3545", fontSize: 12 }}
                >
                  {errors.accessCode}
                </div>
              )}
              <Button
                type="submit"
                className="btn-lg btn-secondary btn-round"
                style={{
                  backgroundColor: page.primary_color,
                  minWidth: "200px",
                }}
              >
                {isSubmitting ? (
                  <Spinner color="default" variant="light" size="sm" />
                ) : (
                  "Submit"
                )}
              </Button>
            </Form>
          )}
        </Formik>
      </Col>
      <SweetAlert
        title="Request Access"
        onConfirm={() => console.log("")}
        onCancel={() => setShowModal(false)}
        show={showModal}
        showConfirm={false}
        showCancel={false}
      >
        <p className="mt-2 mb-4">
          In order to access this item you must request access from the founder
          by submitting your name and email address. The founder will be
          notified of you interest and will reach out to you with more
          information
        </p>
        <Formik
          initialValues={{
            firstName: "",
            lastName: "",
            email: "",
          }}
          validationSchema={yup.object().shape({
            firstName: yup.string().required("First name is required."),
            lastName: yup.string().required("Last name is required."),
            email: yup
              .string()
              .email("Please enter a valid email.")
              .required("Email is required."),
          })}
          onSubmit={async (values, { setSubmitting }) => {
            await requestStandaloneAccess(values);
            setSubmitting(false);
          }}
        >
          {({
            values,
            errors,
            touched,
            isSubmitting,
            setFieldValue,
            handleReset,
          }) => (
            <Form>
              <Row className="mb-3">
                <Col className="text-left">
                  <Label htmlFor="firstName">First Name</Label>
                  <Field
                    name="firstName"
                    type="text"
                    className="form-control"
                    placeholder="First Name"
                    value={values.firstName}
                  />
                  {touched.firstName && errors.firstName && (
                    <div
                      className="error mt-1"
                      style={{ color: "#dc3545", fontSize: 12 }}
                    >
                      {errors.firstName}
                    </div>
                  )}
                </Col>
                <Col className="text-left">
                  <Label htmlFor="lastName">Last Name</Label>
                  <Field
                    name="lastName"
                    type="text"
                    className="form-control"
                    placeholder="Last Name"
                    value={values.lastName}
                  />
                  {touched.lastName && errors.lastName && (
                    <div
                      className="error mt-1"
                      style={{ color: "#dc3545", fontSize: 12 }}
                    >
                      {errors.lastName}
                    </div>
                  )}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col className="text-left">
                  <Label htmlFor="email">Email</Label>
                  <Field
                    name="email"
                    type="text"
                    className="form-control"
                    placeholder="email_address@provider.com"
                    value={values.email}
                  />
                  {touched.email && errors.email && (
                    <div
                      className="error mt-1"
                      style={{ color: "#dc3545", fontSize: 12 }}
                    >
                      {errors.email}
                    </div>
                  )}
                </Col>
              </Row>
              <div className="d-flex justify-content-between">
                <Button
                  outline
                  color="secondary"
                  className="mb-0 btn-outline-default"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </Button>
                <Button className="mb-0 btn-success" type="submit">
                  Request Access
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </SweetAlert>
    </Container>
  );
};

export default Private;
